import {Link} from "react-router-dom";
import {useCallback, useEffect, useRef, useState} from "react";
import {useUserContext} from "../../providers/UserContext";
import {Chat, Group, WorkspaceMember, TypesenseSearchResult, SidebarItem} from "../../lib/types";
import classNames from "classnames";
import {useChatListContext} from "../../providers/ChatListProvider";
import {truncate} from "../../lib/utils/formatting";
import {timeDifference} from "../../lib/utils/time";

import {Icon} from "../common/Icon";
import {debounce} from "lodash";
import {getFunctions, httpsCallable} from "firebase/functions";
import {getButtonClassName} from "../common/Button";
import {getRef} from "../../lib/database";
import {onValue} from "firebase/database";
import {useLayoutContext} from "../../providers/LayoutProvider";
import {ChatListItem} from "./ChatListItem";

export const ChatList = ({}: {}) => {
  const {isMobileResolution, isSidebarOpen, setSidebarVisibility} = useLayoutContext();

  const {activeChatId, groups, activeWorkspace, user, getMember, sidebar} = useUserContext();
  const {chatList, title, currentFilter} = useChatListContext();
  const [search, setSearch] = useState("");
  const [searchResults, setSearchResults] = useState<TypesenseSearchResult[]>([]);
  const abortControllerRef = useRef<AbortController | null>(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const searchField = useRef<HTMLInputElement>(null);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [canIPin, setCanIPin] = useState(false);
  const [filterId, setFilterId] = useState<string | null>(null);
  const [pinned, setPinned] = useState<string[]>([]);
  const functions = getFunctions();
  const runSearch = httpsCallable<
    {
      query?: string;
      onlyInbox?: boolean;
      groupFilter?: string | string[];
    },
    { results: TypesenseSearchResult[] }
  >(functions, "searchMessages");

  const searchChats = useCallback(
    debounce((search: string) => {
      const controller = new AbortController();
      abortControllerRef.current = controller;
      let groupFilter: string | string[] = "";
      if (currentFilter?.startsWith("group_")) {
        groupFilter = currentFilter.replace("group_", "");
      }
      if (currentFilter?.startsWith("sidebar_")) {
        groupFilter = currentFilter.replace("sidebar_", "").split("_");
      }
      runSearch({
        query: search.trim(),
        onlyInbox: title === "Inbox",
        groupFilter: groupFilter,
      }).then(result => {
        setSearchResults(result.data.results);
        setSearchLoading(false);
      });
    }, 700),
    [title] // Dependencies
  );

  useEffect(() => {
    setSearchResults([]);
    setSearch("")
  }, [currentFilter]);

  useEffect(() => {
    console.log("chatList rerendered");
  }, []);
  useEffect(() => {
    if (currentFilter?.startsWith("group_")) {
      setFilterId(currentFilter.replace("group_", ""));
    } else if (currentFilter?.startsWith("sidebar_")) {
      setFilterId(currentFilter.replace("sidebar_", ""));
    } else {
      setFilterId(null);
    }
  }, [currentFilter, isMobileResolution]);

  useEffect(() => {
    let unsubscribe: any;
    if (filterId) {
      const chatRef = getRef('pinned/' + filterId)
      unsubscribe = onValue(chatRef, (snapshot) => {
        const data = snapshot.val();
        if (data) {
          console.log("pinned");
          console.log(data);
          setPinned(Object.keys(data));
        } else {
          setPinned([]);
        }
      });
    } else {
      setPinned([]);
    }
    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    }
  }, [filterId]);

  useEffect(() => {
    if (currentFilter?.startsWith("group_")) {
      const groupId = currentFilter.replace("group_", "");
      const group = groups.find(group => group.id === groupId);
      if (group) {
        setCanIPin(true);
      } else {
        setCanIPin(false);
      }
    } else {
      setCanIPin(false);
    }
    if (currentFilter?.startsWith("sidebar_")) {
      setCanIPin(true);
    }
  }, [currentFilter, groups]);


  const getRecipients = useCallback((chat: Chat) => {
    let sidebarItem: null | SidebarItem = null;
    if (currentFilter?.startsWith("sidebar_")) {
      const sidebarId = currentFilter.replace("sidebar_", "");
      if (sidebarId) {
        sidebarItem = sidebar.filter(item => item.id === sidebarId)[0];
      }
    }
    const recipients: {
      type: "group" | "user",
      id: string,
      name: string,
      group?: Group,
      user?: WorkspaceMember,
    }[] = [];
    const membersToFilterOut: string[] = [];
    let isThereAPublicGroup = false;
    Object.keys(chat.groupPermissions || {}).forEach(groupId => {
      const group = groups.find(group => group.id === groupId);
      if (group) {
        recipients.push({
          type: "group",
          id: group.id,
          name: group.name,
          group,
        });
        if(group.visibility === "public") {
          isThereAPublicGroup = true;
        }
        membersToFilterOut.push(...Object.keys(group.permissions));
      }
    });
    if (activeWorkspace && !isThereAPublicGroup) {
      Object.keys(chat.userPermissions || {}).forEach(userId => {
        const user = activeWorkspace.members[userId];
        if (chat.authorId !== user.id && !membersToFilterOut.includes(user.id)) {
          recipients.push({
            type: "user",
            id: user.id,
            name: user.displayName,
            user,
          });
        }
      });
    }
    const author = getMember(chat.authorId);
    if (author && !isThereAPublicGroup && !membersToFilterOut.includes(author.id)) {
      recipients.push({
        type: "user",
        id: author.id,
        name: author.displayName,
        user: author,
      })
    }
    return recipients.filter(recipient => {
      return recipient.id !== user?.uid &&
        (!sidebarItem || !sidebarItem.meaningfulGroups?.includes(recipient.id)) &&
        (!sidebarItem || !sidebarItem.meaningfulUsers?.includes(recipient.id))
    });

  }, [groups, activeWorkspace, user, currentFilter, sidebar]);

  const [fixedChats, setFixedChats] = useState<Chat[]>([]);
  const [periods, setPeriods] = useState<{ title: string, chats: Chat[] }[]>([]);

  useEffect(() => {
    const fixedChats = chatList.filter(chat => {
      return !!(filterId && pinned.includes(chat.id));
    })

    setFixedChats(fixedChats);
    const chatListChats = chatList.filter(chat => !fixedChats.find(fixedChat => fixedChat.id === chat.id)).sort((a, b) => (a.lastMessage && b.lastMessage) ? b.lastMessage.timestamp - a.lastMessage.timestamp : 0);


    const todayChats = chatListChats.filter(chat => {
      return chat.lastMessage && new Date(chat.lastMessage.timestamp).toDateString() === new Date().toDateString();
    });

    const lastWeekChats = chatListChats.filter(chat => {
      return chat.lastMessage && !todayChats.find(c => c.id === chat.id) && new Date(chat.lastMessage.timestamp) > new Date(new Date().setDate(new Date().getDate() - 7));
    });

    const olderChats = chatListChats.filter(chat => {
      return chat.lastMessage && !todayChats.find(c => c.id === chat.id) && !lastWeekChats.find(c => c.id === chat.id);
    });

    const periods = [
      {title: "Today", chats: todayChats},
      {title: "Last 7 days", chats: lastWeekChats},
      {title: "Older", chats: olderChats},
    ].filter(period => period.chats.length > 0);
    setPeriods(periods);
  }, [chatList, pinned, filterId]);

  const isActive = (chat: Chat | string) => {
    if (typeof chat === "string") {
      return activeChatId === chat;
    }
    return activeChatId === chat.id;
  }
  const [now, setNow] = useState(Date.now());
  setInterval(() => {
    setNow(Date.now());
  }, 60000);

  const [scrollDirection, setScrollDirection] = useState<"up"|"down"|null>(null);
  const [position, setPosition] = useState(0);
  const timer = useRef<NodeJS.Timeout | null>(null);
  const scrollHandler = (e: any ) => {
    if(!e.currentTarget) return;

    const pos = position;
    const newPos = (e.currentTarget as HTMLDivElement).scrollTop;
    if(timer.current) {
      window.clearTimeout(timer.current);
    }
    if(newPos >= pos) {
      setScrollDirection("down");
    } else {
      setScrollDirection("up");
    }
    timer.current = setTimeout(() => {
      setScrollDirection(null);
    }, 3000)
    setPosition(newPos);
  };
  const getUrl = useCallback((chatId: string) => {
    if(currentFilter?.startsWith("inbox")) {
      return "/inbox/chat/" + chatId;
    }
    if(currentFilter?.startsWith("group")) {
      return "/group/" + currentFilter.replace("group_", "") + "/chat/" + chatId;
    }
    if(currentFilter?.startsWith("sidebar")) {
      return "/chats/" + currentFilter.replace("sidebar_", "") + "/chat/" + chatId;
    }
    return "/chat/" + chatId;
  },[currentFilter]);

  return (
    <div className={
      classNames(
        "h-full flex flex-col",
        isMobileResolution ? "w-full" : "w-[496px]"
      )
    }>
       <div className={
         classNames("list px-4 pt-4 pb-3 -mt-[1px] flex-shrink-0 flex justify-between w-full borded border-b border-b-gray-6 items-center")
       }>
        <h2
          className={"font-style-headline-3 w-[calc(100%+3px)] flex gap-2 items-center"}>
          {isMobileResolution && (
            <button onClick={() => setSidebarVisibility(!isSidebarOpen)} className={"flex items-center"}>
              <Icon name={isSidebarOpen ? "close" : "menu"} size={"medium"}/>
            </button>
          )}
          {title}
        </h2>
         <div className={"basis-[120px] flex justify-end"}>
           <Link to={"/compose"} className={getButtonClassName()+" !px-3"}>
             <span className={"inline-block"}>New chat</span>
           </Link>
         </div>
      </div>
       <div className="search relative">
        <div className="absolute top-[14px] left-4 text-gray-4">
            <Icon name={"search"} size="small"/>
        </div>
        <input type="text" ref={searchField} placeholder="Search" value={search} className="w-full pr-4 pl-10 py-4 border-b border-b-gray-6 focus:shadow-[inset_0_0_3px_rgba(0,0,0,.12)] focus:outline-0 bg-transparent" onChange={(event) => {
          setSearch(event.currentTarget.value);
          setSearchLoading(true);
          if(!event.currentTarget.value) {
            setSearchResults([]);
          } else {
            searchChats(event.currentTarget.value);
          }
        }}/>
      </div>
      {search ? (
        <div key={"search-" + search} className={"search-results"}>
          <h2 className={"font-style-headline-3 px-4 py-2"}>
            {searchLoading ? `Searching for "${search}"...` : `${searchResults.length} results for your search "${search}"`}
          </h2>
          {searchResults.map(result => {
            return (
              <Link key={result.document.chatId}
                    to={getUrl(result.document.chatId) + "?message=" + result.document.id}
                    className={classNames(
                      "flex w-full relative group items-center justify-between gap-4 transition-all border-t border-t-gray-7 last:border-b last:border-b-gray-7 group",
                      activeChatId === result.document.chatId ? " border-gray-7" : "bg-transparent ",
                    )}
              >
                <div className={classNames(
                  "flex relative flex-col leading-1 hover:bg-white transition-all border border-transparent hover:bg-white w-[calc(100%-24px)] rounded-md px-[12px] mx-[12px] py-[8px] my-[8px]",
                  activeChatId === result.document.chatId && "shadow-elevation-2 bg-white"
                )}>
                  <div className={"w-full items-center flex breadcrumbs max-w-full overflow-x-hidden"}>
                    <div className="flex-grow flex gap-1">
                      <span className={"font-style-label-md flex gap-1 text-gray-4"}>
                        <span
                          dangerouslySetInnerHTML={{__html: result.highlight?.chatTitle?.snippet || result.document.chatTitle}}/>
                      </span>
                    </div>
                  </div>
                  <div className={"font-style-body-md mt-1 flex justify-between text-gray-4"}>
                    <div className="w-[calc(100%-70px]">
                      <span className="">
                        <span className={"mr-1"}>
                          {result.document.authorName}:
                        </span>
                        <span
                          dangerouslySetInnerHTML={{__html: result.highlight?.plainText?.snippet || truncate(result.document.plainText, 120)}}/>


                      </span>
                    </div>
                    <div className={"w-[50px] ml-auto text-right font-style-body-sm text-gray-4"}>
                      {result.document.timestamp && timeDifference(now, result.document.timestamp)}
                    </div>
                  </div>
                </div>
              </Link>
            )
          })}
        </div>
      ) : (
        <div className={
          classNames(
            "flex-grow w-full overflow-auto",
          )
        } onScroll={(e) => scrollHandler(e)}>
          {fixedChats.length > 0 && (<div>
              <h3 className={"font-style-label-sm text-gray-4 mt-6 mb-1 mx-4 pb-2 relative inline-block"}>
                Pinned
                <hr className={"w-full bg-gray-4 absolute bottom-[-5px] z-[9] "}/>
              </h3>
              <div>

          {fixedChats.map((chat) => {
            return (
              <ChatListItem
                pinned={true}
                key={chat.id}
                chat={chat}
                setActiveDropdown={setActiveDropdown}
                activeDropdown={activeDropdown}
                now={now}
                canPin={canIPin}
              />
            )
          })}
          </div></div>
          )   }
          {periods.map(period => {
            return (
              <div key={period.title}>
                <h3 className={"font-style-label-sm text-gray-4 mt-6 mb-1 mx-4 pb-2 relative inline-block"}>
                  {period.title}
                  <hr className={"w-full bg-gray-4 absolute bottom-[-5px] z-[9] "}/>
                </h3>
                {period.chats.map((chat) => {
                  return (
                    <ChatListItem
                      pinned={false}
                      key={chat.id}
                      chat={chat}
                      setActiveDropdown={setActiveDropdown}
                      activeDropdown={activeDropdown}
                      now={now}
                      canPin={canIPin}

                    />
                  )
                })}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}